import { useState } from 'react';
import type { ChangeEventHandler, FocusEventHandler, ComponentType } from 'react';
import { Select } from 'antd';
import {
  CrTeamIcon,
  HierarchyNodesIcon,
  LocationPinIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '../../../assets/icons';
import { Field, SectionHeading, fieldCls } from './formFields';
import PhoneRow from './PhoneRow';
import AssignRoleModal from './AssignRoleModal';
import {
  ROLE_SCOPES,
  TITLES,
  toSelectOptions,
  type AssignedRole,
  type RoleScope,
  type UserFormValues,
} from './types';

type SetField = (field: string, value: unknown) => void;

interface Props {
  values: UserFormValues;
  setFieldValue: SetField;
  handleChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  errorOf: (name: keyof UserFormValues) => string | undefined;
  organization?: number;
  roleOptions: { value: number; label: string }[];
  departmentOptions: { value: string; label: string }[];
  designationOptions: { value: string; label: string }[];
}

/** Icon shown in each role card's coloured square, by scope type. */
const SCOPE_ICON: Record<RoleScope, ComponentType<{ className?: string }>> = {
  functional: CrTeamIcon,
  territorial: LocationPinIcon,
  hybrid: HierarchyNodesIcon,
};
const scopeMeta = (s: RoleScope) => ROLE_SCOPES.find((x) => x.key === s) ?? ROLE_SCOPES[0];

/**
 * Client Team User (organization) create form — Primary Information + a multi-role
 * "Assigned Roles & Scopes" manager where each role carries a scope type (Functional /
 * Territorial / Hybrid), a department and an optional location (Figma 1427-2350).
 */
export default function ClientTeamUserForm({
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  errorOf,
  organization,
  roleOptions,
  departmentOptions,
  designationOptions,
}: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AssignedRole | null>(null);

  const roles = values.assignedRoles;
  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (r: AssignedRole) => {
    setEditing(r);
    setModalOpen(true);
  };
  const removeRole = (id: string) =>
    setFieldValue('assignedRoles', roles.filter((r) => r.id !== id));
  const saveRole = (role: AssignedRole) => {
    if (role.id) {
      setFieldValue('assignedRoles', roles.map((r) => (r.id === role.id ? role : r)));
    } else {
      // Assign a stable client id based on the running count (no Date/random needed).
      const used = new Set(roles.map((r) => r.id));
      let n = roles.length;
      let id = `r${n}`;
      while (used.has(id)) id = `r${++n}`;
      setFieldValue('assignedRoles', [...roles, { ...role, id }]);
    }
    setModalOpen(false);
    setEditing(null);
  };

  return (
    <div className="flex flex-col gap-8">
      {/* ── Primary Information ─────────────────────────────────────────── */}
      <div className="flex flex-col gap-4">
        <SectionHeading>Primary Information</SectionHeading>
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="w-full md:w-[140px]">
            <Field label="Title">
              <Select
                className="w-full"
                placeholder="e.g. Rajesh"
                value={values.title || undefined}
                onChange={(v) => setFieldValue('title', v ?? '')}
                options={toSelectOptions(TITLES)}
                allowClear
              />
            </Field>
          </div>
          <Field label="Name" htmlFor="firstName" required error={errorOf('firstName')}>
            <input
              id="firstName"
              name="firstName"
              placeholder="e.g. Kumar"
              value={values.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldCls(errorOf('firstName'))}
            />
          </Field>
          <Field label="Last Name" htmlFor="lastName">
            <input
              id="lastName"
              name="lastName"
              placeholder="e.g. Sharma"
              value={values.lastName}
              onChange={handleChange}
              className={fieldCls()}
            />
          </Field>
          <Field label="Email Address" htmlFor="email" error={errorOf('email')}>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="rajesh.k@chunaav.gov.in"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldCls(errorOf('email'))}
            />
          </Field>
        </div>

        <PhoneRow
          contact={values.contact}
          landline={values.landline}
          landlineExt={values.landlineExt}
          whatsapp={values.whatsapp}
          whatsappIndex={values.whatsappIndex}
          contactError={errorOf('contact')}
          setFieldValue={setFieldValue}
        />
      </div>

      {/* ── Assigned Roles & Scopes ─────────────────────────────────────── */}
      <div className="flex flex-col gap-4 border-t border-[#f1f5f9] pt-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <SectionHeading>Assigned Roles &amp; Scopes</SectionHeading>
              <span className="rounded-full bg-[#eff6ff] px-2.5 py-0.5 text-[11px] font-semibold text-brand">
                {roles.length} {roles.length === 1 ? 'Role' : 'Roles'} Active
              </span>
            </div>
            <p className="text-xs text-[#64748b]">
              A user can have multiple roles across Functional, Territorial, or Hybrid scopes
              without overwriting existing assignments.
            </p>
          </div>
          <button
            type="button"
            onClick={openAdd}
            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-brand px-3 py-1.5 text-xs font-semibold text-brand transition-colors hover:bg-brand-shade/60"
          >
            <PlusIcon className="size-3.5" />
            Add Role
          </button>
        </div>

        {roles.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#e2e8f0] bg-[#f8fafc] px-4 py-8 text-center text-sm text-[#94a3b8]">
            No roles assigned yet. Click <span className="font-medium text-brand">Add Role</span> to
            grant access.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {roles.map((r) => {
              const meta = scopeMeta(r.scopeType);
              const Icon = SCOPE_ICON[r.scopeType];
              return (
                <div
                  key={r.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-[#e2e8f0] bg-white p-4"
                >
                  <div className="flex min-w-0 items-center gap-3.5">
                    <span
                      className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${meta.iconWrap}`}
                    >
                      <Icon className="size-5" />
                    </span>
                    <div className="flex min-w-0 flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={`rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.3px] ${meta.badge}`}
                        >
                          {meta.label}
                        </span>
                        <span className="truncate text-sm font-semibold text-ink">
                          {r.roleName || '—'}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[#64748b]">
                        {r.department && (
                          <>
                            <span>
                              Department: <span className="text-[#334155]">{r.department}</span>
                            </span>
                            <span className="text-[#cbd5e1]">•</span>
                          </>
                        )}
                        {r.scopeType === 'functional' || !r.locationLabel ? (
                          <span>No location required (Global Scope)</span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <LocationPinIcon className="size-3.5 text-[#94a3b8]" />
                            {r.locationLabel}
                            {r.boundaryAuthority && (
                              <span className="text-[#94a3b8]">
                                {' '}
                                ({r.boundaryAuthority.toUpperCase()} Standard)
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <button
                      type="button"
                      aria-label="Edit role"
                      onClick={() => openEdit(r)}
                      className="flex size-8 items-center justify-center rounded-lg text-[#64748b] transition-colors hover:bg-brand-shade hover:text-brand"
                    >
                      <PencilIcon className="size-4" />
                    </button>
                    <button
                      type="button"
                      aria-label="Delete role"
                      onClick={() => removeRole(r.id)}
                      className="flex size-8 items-center justify-center rounded-lg text-[#64748b] transition-colors hover:bg-[#fee2e2] hover:text-[#dc2626]"
                    >
                      <TrashIcon className="size-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AssignRoleModal
        open={modalOpen}
        organization={organization}
        roleOptions={roleOptions}
        departmentOptions={departmentOptions}
        designationOptions={designationOptions}
        initial={editing}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSave={saveRole}
      />
    </div>
  );
}
